
import { useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useContentManager } from '@/hooks/useContentManager';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function LinkedInOAuthHandler() {
  const { toast } = useToast();
  const { navigateTo } = useContentManager();
  const { user } = useAuth();
  
  useEffect(() => {
    // Check if this is a callback from LinkedIn OAuth
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    const errorDescription = url.searchParams.get('error_description');
    const provider = url.searchParams.get('provider');
    
    console.log('[LinkedIn OAuth Handler] Checking for OAuth callback:', { 
      hasCode: !!code, 
      hasState: !!state, 
      error, 
      errorDescription,
      provider,
      path: window.location.pathname,
      href: window.location.href,
      origin: window.location.origin
    });
    
    // Skip processing if this is a Supabase OIDC authentication flow
    if (provider === 'linkedin_oidc') {
      console.log('[LinkedIn OAuth Handler] Detected Supabase OIDC authentication flow, not processing');
      return;
    }
    
    // Only process if this looks like an OAuth content posting callback (has code or error)
    if ((code && state) || error) {
      console.log('[LinkedIn OAuth Handler] LinkedIn OAuth callback detected');
      
      // Make sure we have a user before proceeding
      if (!user && code && state) {
        console.log('[LinkedIn OAuth Handler] User not authenticated, saving OAuth state for later processing');
        try {
          // Store the OAuth info to process after authentication
          sessionStorage.setItem('pendingLinkedInOAuth', JSON.stringify({
            code,
            state,
            timestamp: Date.now()
          }));
          
          toast({
            title: "Authentication required",
            description: "Please sign in to complete LinkedIn connection",
          });
        } catch (e) {
          console.error('[LinkedIn OAuth Handler] Error storing pending OAuth state:', e);
        }
        return;
      }
      
      if (code && state) {
        // Process the successful OAuth callback
        handleSuccessfulCallback(code, state);
      } else if (error) {
        // Handle OAuth error
        handleOAuthError(error, errorDescription || '');
      }
    } else if (user) {
      // Check for pending OAuth flow from before the user was authenticated
      try {
        const pendingOAuth = sessionStorage.getItem('pendingLinkedInOAuth');
        if (pendingOAuth) {
          console.log('[LinkedIn OAuth Handler] Found pending OAuth flow, processing');
          const { code, state } = JSON.parse(pendingOAuth);
          
          if (code && state) {
            // Remove the pending OAuth info to prevent reprocessing
            sessionStorage.removeItem('pendingLinkedInOAuth');
            // Process the successful OAuth callback
            handleSuccessfulCallback(code, state);
            return;
          }
        }
      } catch (e) {
        console.error('[LinkedIn OAuth Handler] Error checking for pending OAuth flow:', e);
      }
    }
  }, [user]);
  
  const handleSuccessfulCallback = async (code: string, state: string) => {
    try {
      toast({
        title: "Processing LinkedIn authorization",
        description: "Please wait while we connect your LinkedIn account...",
      });
      
      console.log('[LinkedIn OAuth Handler] Starting LinkedIn OAuth token exchange');
      console.log('[LinkedIn OAuth Handler] State:', state);
      
      let userId = user?.id;
      
      try {
        const decodedState = JSON.parse(atob(state));
        console.log('[LinkedIn OAuth Handler] Decoded state:', decodedState);
        
        // Use userId from state if available and no current user
        if (!userId && decodedState?.userId) {
          userId = decodedState.userId;
          console.log('[LinkedIn OAuth Handler] Using userId from state:', userId);
        }
      } catch (e) {
        console.error('[LinkedIn OAuth Handler] Error decoding state:', e);
      }
      
      if (!userId) {
        console.error('[LinkedIn OAuth Handler] No user ID available for token storage');
        throw new Error('User ID not available');
      }
      
      // Call the edge function to exchange the code for an access token
      const { data, error } = await supabase.functions.invoke('linkedin-oauth', {
        body: { 
          code, 
          state, 
          userId,
          redirectUri: window.location.origin + '/auth' // Use the auth path as redirect URI
        },
      });
      
      if (error) {
        console.error('[LinkedIn OAuth Handler] LinkedIn edge function error:', error);
        throw new Error(`Edge function error: ${error.message}`);
      }
      
      console.log('[LinkedIn OAuth Handler] LinkedIn OAuth response:', data);
      
      if (data?.success) {
        toast({
          title: "LinkedIn connected successfully",
          description: "Your LinkedIn account has been connected.",
        });
      } else {
        throw new Error(data?.message || "Unknown error occurred");
      }
    } catch (err) {
      console.error("[LinkedIn OAuth Handler] Error processing LinkedIn OAuth:", err);
      toast({
        title: "Connection failed",
        description: err instanceof Error ? err.message : "Failed to connect LinkedIn account",
        variant: "destructive",
      });
    } finally {
      // Clear the URL params to avoid issues on refresh
      const cleanUrl = window.location.href.split('?')[0];
      console.log('[LinkedIn OAuth Handler] Cleaning URL:', cleanUrl);
      window.history.replaceState({}, document.title, cleanUrl);
      
      // Navigate to the dashboard after processing
      console.log('[LinkedIn OAuth Handler] Navigating to LinkedIn dashboard');
      navigateTo('/dashboard/linkedin');
    }
  };
  
  const handleOAuthError = (error: string, errorDescription: string) => {
    console.error(`[LinkedIn OAuth Handler] LinkedIn OAuth error: ${error} - ${errorDescription}`);
    
    toast({
      title: "LinkedIn connection failed",
      description: errorDescription || "Could not connect to LinkedIn. Please try again.",
      variant: "destructive",
    });
    
    // Clear the URL params to avoid issues on refresh
    const cleanUrl = window.location.href.split('?')[0];
    console.log('[LinkedIn OAuth Handler] Cleaning URL after error:', cleanUrl);
    window.history.replaceState({}, document.title, cleanUrl);
    
    // Navigate back to the LinkedIn dashboard
    console.log('[LinkedIn OAuth Handler] Navigating to LinkedIn dashboard after error');
    navigateTo('/dashboard/linkedin');
  };
  
  return null; // This component doesn't render anything
}
